// src/components/Careers.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
  Divider,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import VerifiedIcon from "@mui/icons-material/Verified";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import TimelineIcon from "@mui/icons-material/Timeline";
import SecurityIcon from "@mui/icons-material/Security";
import StarsIcon from "@mui/icons-material/Stars";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

// ✅ SEO hooks (keep same structure used in your app)
import { useSEO, useJsonLD } from "./SEO"; // adjust path if needed

// ---- Theme tokens (dark, Binance-like accent)
const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const SURFACE_SOFT = "rgba(38,50,56,0.65)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const ACCENT = "#F0B90B";

const CATEGORIES = [
  "All",
  "Engineering",
  "AI/ML",
  "Product",
  "Design",
  "Security",
  "Support",
  "Operations",
];

// ---- Generic, high-level roles (non-revealing)
const JOBS = [
  {
    id: "frontend-engineer",
    title: "Frontend Engineer",
    dept: "Engineering",
    level: "Mid–Senior",
    location: "Global / Flexible",
    tags: ["Front-end", "Web UI", "Accessibility", "Quality"],
    intro:
      "Craft user-facing experiences with clarity, consistency, and reliable performance across devices.",
  },
  {
    id: "mobile-engineer",
    title: "Mobile Engineer",
    dept: "Engineering",
    level: "Mid–Senior",
    location: "Global / Flexible",
    tags: ["Mobile", "Animations", "UX", "Reliability"],
    intro:
      "Deliver a smooth mobile experience with intuitive navigation and robust interactions.",
  },
  {
    id: "ai-ml-generalist",
    title: "AI/ML Generalist",
    dept: "AI/ML",
    level: "Senior",
    location: "Global / Flexible",
    tags: ["AI/ML", "Evaluation", "Signals", "Impact"],
    intro:
      "Shape practical, user-centric intelligence features with careful evaluation and responsible delivery.",
  },
  {
    id: "backend-generalist",
    title: "Backend Generalist",
    dept: "Engineering",
    level: "Mid–Senior",
    location: "Global / Flexible",
    tags: ["APIs", "Data", "Observability", "Correctness"],
    intro:
      "Build simple, dependable services with a focus on correctness, clarity, and long-term maintainability.",
  },
  {
    id: "product-manager",
    title: "Product Manager",
    dept: "Product",
    level: "Senior",
    location: "Global / Flexible",
    tags: ["Roadmaps", "Discovery", "Metrics", "Alignment"],
    intro:
      "Own outcomes end-to-end: clarify goals, guide execution, and align teams around measurable value.",
  },
  {
    id: "security-analyst",
    title: "Security Analyst",
    dept: "Security",
    level: "Mid–Senior",
    location: "Global / Flexible",
    tags: ["Security", "Risk", "Controls", "Review"],
    intro:
      "Promote a security-minded culture and support practical guardrails that build user trust.",
  },
];

// ---- Principles / Benefits (neutral, universal)
const PRINCIPLES = [
  {
    icon: <ManageAccountsIcon />,
    title: "Ownership with Clarity",
    text:
      "We value clear scope, thoughtful prioritization, and steady progress that compounds over time.",
  },
  {
    icon: <QueryStatsIcon />,
    title: "Judgment & Rigor",
    text:
      "We favor measured decisions, practical iteration, and a healthy respect for data and user feedback.",
  },
  {
    icon: <Diversity3Icon />,
    title: "Inclusive Collaboration",
    text:
      "We collaborate with kindness and candor, creating space for different perspectives to shape better outcomes.",
  },
  {
    icon: <AutoAwesomeIcon />,
    title: "Craft & Reliability",
    text:
      "We care about details, stability, and the long-term quality of user experiences.",
  },
];

const BENEFITS = [
  {
    icon: <FavoriteBorderIcon />,
    title: "Well-Being",
    text:
      "Flexible time-off, a healthy pace, and support for sustainable productivity.",
  },
  {
    icon: <VerifiedIcon />,
    title: "Growth",
    text:
      "Learning support, constructive feedback loops, and clear scope expansion.",
  },
  {
    icon: <PeopleAltIcon />,
    title: "Global-Friendly",
    text:
      "Distributed collaboration with async clarity and respectful time zones.",
  },
  {
    icon: <SecurityIcon />,
    title: "Stability",
    text: "Pragmatic processes that enable teams to focus on meaningful work.",
  },
];

// ---- Small UI helpers
function SectionTitle({ title, subtitle }) {
  return (
    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ color: TEXT, fontWeight: 900 }}>
          {title}
        </Typography>
        <Box
          sx={{
            height: 3,
            width: 28,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${ACCENT}, rgba(240,185,11,0.25))`,
          }}
        />
      </Stack>
      {subtitle && (
        <Typography sx={{ color: MUTED, maxWidth: 900 }}>{subtitle}</Typography>
      )}
    </Stack>
  );
}

function IconBadge({ children }) {
  return (
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "999px",
        bgcolor: "rgba(255,255,255,0.06)",
        border: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: ACCENT,
        "& svg": { fontSize: 22 },
        boxShadow: "0 0 0 2px rgba(240,185,11,0.12)",
        position: "relative",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(240,185,11,0.20), transparent 45%)",
        },
      }}
    >
      {children}
    </Box>
  );
}

function Row({ icon, text }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <IconBadge>{icon}</IconBadge>
      <Typography sx={{ color: MUTED }}>{text}</Typography>
    </Stack>
  );
}

export default function Careers() {
  // ✅ SEO (generic, non-revealing)
  useSEO({
    title: "Careers — 1CryptoX | Grow with a Product-Led, User-Focused Team",
    description:
      "Join a team that values clarity, craft, and responsible delivery. Explore roles across Engineering, AI/ML, Product, Security, and more.",
    keywords: [
      "careers",
      "jobs",
      "engineering roles",
      "ai roles",
      "product roles",
      "security roles",
      "design roles",
    ],
    canonical: "https://www.1cryptox.com/careers",
    og: {
      title: "Careers — 1CryptoX",
      description:
        "Build user-focused experiences with a team that values quality and clarity.",
      url: "https://www.1cryptox.com/careers",
      image: "https://www.1cryptox.com/og/careers-card.jpg",
      siteName: "1CryptoX",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "Careers — 1CryptoX",
      description:
        "Explore high-level roles across Engineering, AI/ML, Product, and more.",
      image: "https://www.1cryptox.com/og/careers-card.jpg",
    },
  });

  // ✅ JSON-LD (ItemList + JobPosting) with generic content
  useJsonLD("jobs-list", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: JOBS.map((j, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "JobPosting",
        title: j.title,
        employmentType: "FULL_TIME",
        hiringOrganization: {
          "@type": "Organization",
          name: "1CryptoX",
          sameAs: "https://www.1cryptox.com",
        },
        jobLocationType: "TELECOMMUTE",
        description: j.intro,
        validThrough: "2026-12-31",
      },
    })),
  });

  const [tab, setTab] = useState(0);
  const [q, setQ] = useState("");

  const filteredJobs = useMemo(() => {
    const cat = CATEGORIES[tab];
    return JOBS.filter((j) => {
      const inCat = cat === "All" ? true : j.dept === cat;
      const inQ =
        !q ||
        j.title.toLowerCase().includes(q.toLowerCase()) ||
        j.tags.join(" ").toLowerCase().includes(q.toLowerCase()) ||
        j.intro.toLowerCase().includes(q.toLowerCase());
      return inCat && inQ;
    });
  }, [tab, q]);

  const onApply = (job) => {
    window.location.href = `/careers/apply?job=${encodeURIComponent(job.id)}`;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* ---------- HERO ---------- */}
        <Card
          sx={{
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Subtle highlight band */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(70% 60% at 80% 20%, rgba(240,185,11,0.12) 0%, rgba(240,185,11,0.02) 100%)",
              pointerEvents: "none",
            }}
          />
          <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 900 }}
                >
                  Careers
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, color: TEXT }}>
                  Grow with a Product-Led, User-Focused Team
                </Typography>
                <Typography sx={{ mt: 1, color: MUTED, maxWidth: 900 }}>
                  We build with clarity, care, and responsible delivery. If you
                  value steady progress, thoughtful iteration, and a respectful
                  remote-first culture, you’ll feel at home here. Your work will
                  shape reliable experiences used by a global audience.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, flexWrap: "wrap" }}
                >
                  <Chip
                    icon={<StarsIcon />}
                    label="High Standards"
                    sx={{
                      bgcolor: "rgba(240,185,11,0.12)",
                      color: ACCENT,
                      border: `1px solid rgba(240,185,11,0.25)`,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    icon={<TrendingUpIcon />}
                    label="Measured Impact"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: TEXT,
                      border: `1px solid ${BORDER}`,
                    }}
                  />
                  <Chip
                    icon={<LightbulbIcon />}
                    label="Pragmatic Innovation"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: TEXT,
                      border: `1px solid ${BORDER}`,
                    }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.25 } }}>
                    <Typography sx={{ color: TEXT, fontWeight: 800, mb: 1 }}>
                      What We Value
                    </Typography>
                    <Stack spacing={0.75}>
                      <Row
                        icon={<ManageAccountsIcon />}
                        text="Clear scope, reliable ownership, and consistent execution."
                      />
                      <Row
                        icon={<TimelineIcon />}
                        text="Balanced ambition with careful prioritization and iteration."
                      />
                      <Row
                        icon={<SecurityIcon />}
                        text="Respect for users, thoughtful guardrails, and quality over shortcuts."
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ---------- PRINCIPLES & BENEFITS ---------- */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <SectionTitle
                  title="Principles"
                  subtitle="A few shared beliefs help us make better decisions together and sustain quality at scale."
                />
                <Grid container spacing={2}>
                  {PRINCIPLES.map((v) => (
                    <Grid item xs={12} sm={6} key={v.title}>
                      <Stack direction="row" spacing={1.25}>
                        <IconBadge>{v.icon}</IconBadge>
                        <Box>
                          <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                            {v.title}
                          </Typography>
                          <Typography sx={{ color: MUTED }}>
                            {v.text}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <SectionTitle
                  title="Working with Us"
                  subtitle="We aim for sustainable pace, clear expectations, and room to grow."
                />
                <Stack spacing={1.25}>
                  {BENEFITS.map((b) => (
                    <Stack direction="row" spacing={1.25} key={b.title}>
                      <IconBadge>{b.icon}</IconBadge>
                      <Box>
                        <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                          {b.title}
                        </Typography>
                        <Typography sx={{ color: MUTED }}>{b.text}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ---------- FILTERS (Tabs + Search) ---------- */}
        <Card
          sx={{
            mt: 2,
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    ".MuiTab-root": {
                      color: MUTED,
                      textTransform: "none",
                      fontWeight: 800,
                      letterSpacing: 0.2,
                    },
                    ".MuiTab-root:hover": { color: ACCENT },
                    ".Mui-selected": { color: ACCENT },
                    ".MuiTabs-indicator": {
                      backgroundColor: ACCENT,
                      height: 3,
                    },
                    ".MuiTab-root.Mui-focusVisible": { color: ACCENT },
                  }}
                >
                  {CATEGORIES.map((c) => (
                    <Tab key={c} label={c} />
                  ))}
                </Tabs>
              </Grid>

              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Search roles, skills, or keywords…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ---------- JOB CARDS ---------- */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {filteredJobs.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
                <CardContent>
                  <Typography sx={{ color: MUTED }}>
                    No roles match your search. Try a broader query or a
                    different category.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {filteredJobs.map((job) => (
            <Grid key={job.id} item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: SURFACE_SOFT,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                  backdropFilter: "blur(6px)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <IconBadge>
                      <WorkOutlineIcon />
                    </IconBadge>
                    <Box flex={1}>
                      <Typography
                        variant="h6"
                        sx={{ color: TEXT, fontWeight: 900 }}
                      >
                        {job.title}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: "wrap", my: 0.5 }}
                      >
                        <Chip
                          size="small"
                          icon={<PublicIcon />}
                          label={job.location}
                          sx={{
                            bgcolor: "rgba(240,185,11,0.12)",
                            color: ACCENT,
                            border: `1px solid rgba(240,185,11,0.25)`,
                            fontWeight: 700,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<LocationOnIcon />}
                          label={job.dept}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.06)",
                            color: TEXT,
                            border: `1px solid ${BORDER}`,
                          }}
                        />
                        <Chip
                          size="small"
                          label={job.level}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.06)",
                            color: TEXT,
                            border: `1px solid ${BORDER}`,
                          }}
                        />
                      </Stack>

                      <Typography sx={{ color: MUTED, mb: 1 }}>
                        {job.intro}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ flexWrap: "wrap", mb: 1 }}
                      >
                        {job.tags.map((t) => (
                          <Chip
                            key={t}
                            size="small"
                            label={t}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.06)",
                              color: TEXT,
                              border: `1px solid ${BORDER}`,
                            }}
                          />
                        ))}
                      </Stack>

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={() => onApply(job)}
                          sx={{
                            bgcolor: ACCENT,
                            color: "#1C2526",
                            fontWeight: 900,
                            textTransform: "none",
                            px: 2.25,
                            "&:hover": { bgcolor: "#FFD54F" },
                          }}
                        >
                          Apply Now
                        </Button>
                        <Tooltip title="Save & apply later (optional)">
                          <Button
                            variant="outlined"
                            sx={{
                              color: TEXT,
                              borderColor: BORDER,
                              textTransform: "none",
                              fontWeight: 800,
                            }}
                          >
                            Save
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ---------- HOW WE CONNECT ---------- */}
        <Card sx={{ mt: 2, bgcolor: BG, border: `1px solid ${BORDER}` }}>
          <CardContent>
            <SectionTitle
              title="How We Connect"
              subtitle="A simple process that respects your time and focuses on signal over noise."
            />
            <Grid container spacing={2}>
              {[
                {
                  t: "Introduce",
                  d: "Share recent work and what energizes you most.",
                },
                {
                  t: "Converse",
                  d: "Talk about outcomes, collaboration, and expectations.",
                },
                {
                  t: "Explore",
                  d: "A small, relevant exercise — fair and time-bounded.",
                },
                {
                  t: "Decide",
                  d: "Align on scope, growth, and a path that makes sense.",
                },
              ].map((s, i) => (
                <Grid key={i} item xs={12} md={3}>
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: ACCENT, fontWeight: 900 }}>
                      {String(i + 1).padStart(2, "0")}
                    </Typography>
                    <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                      {s.t}
                    </Typography>
                    <Typography sx={{ color: MUTED }}>{s.d}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* ---------- EEO NOTE ---------- */}
        <Card
          sx={{
            mt: 2,
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Typography sx={{ color: TEXT, fontWeight: 900, mb: 0.5 }}>
              Equal Opportunity
            </Typography>
            <Typography sx={{ color: MUTED }}>
              We welcome applicants from all backgrounds. If you need reasonable
              accommodations at any step, let us know how we can help.
            </Typography>
          </CardContent>
        </Card>

        {/* ---------- CALL TO ACTION ---------- */}
        <Box sx={{ mt: 2 }}>
          <Card
            sx={{
              bgcolor: BG,
              border: `1px solid ${BORDER}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(240,185,11,0.05), rgba(240,185,11,0.00))",
                pointerEvents: "none",
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, md: 2.5 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ color: TEXT, fontWeight: 900 }}>
                  Don’t see a perfect role?
                </Typography>
                <Typography sx={{ color: MUTED }}>
                  We’re always excited to meet thoughtful builders. Share your
                  work and what you’d like to explore next.
                </Typography>
              </Stack>
              <Button
                href="/careers/apply?job=open-application"
                variant="contained"
                sx={{
                  bgcolor: ACCENT,
                  color: "#1C2526",
                  fontWeight: 900,
                  textTransform: "none",
                  px: 2.5,
                  "&:hover": { bgcolor: "#FFD54F" },
                }}
              >
                Open Application
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
